import { gsap } from "gsap";
import { Check, X } from "lucide-react";
import { type ReactNode, useId, useLayoutEffect, useRef } from "react";

import { MOTION_DURATION, MOTION_EASE } from "@/shared/components/motion/motion.tokens";
import { usePrefersReducedMotion } from "@/shared/hooks/usePrefersReducedMotion";
import type { VerificationTranslations } from "@/shared/lang/interfaces/verification";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

type VerificationCopyKey = keyof VerificationTranslations;

export type DocumentExample = "ok" | "corner" | "glare" | "angle" | "blurry" | "contrast";
export type SelfieExample = "ok" | "offcenter" | "tilted" | "glasses" | "document" | "crowd";

const DOCUMENT_CAPTIONS: Record<DocumentExample, VerificationCopyKey> = {
  ok: "photo_guide_example_ok",
  corner: "photo_guide_example_corner",
  glare: "photo_guide_example_glare",
  angle: "photo_guide_example_angle",
  blurry: "photo_guide_example_blurry",
  contrast: "photo_guide_example_contrast",
};

const SELFIE_CAPTIONS: Record<SelfieExample, VerificationCopyKey> = {
  ok: "photo_guide_example_ok",
  offcenter: "photo_guide_example_offcenter",
  tilted: "photo_guide_example_tilted",
  glasses: "photo_guide_example_glasses",
  document: "photo_guide_example_holding",
  crowd: "photo_guide_example_crowd",
};

const DOCUMENT_ORDER: readonly DocumentExample[] = ["ok", "corner", "glare", "angle", "blurry", "contrast"];
const SELFIE_ORDER: readonly SelfieExample[] = ["ok", "offcenter", "tilted", "glasses", "document", "crowd"];

/**
 * Each mistake as a pair of states: `right` is the correct photo, `wrong` the mistake. The drawing is the
 * mistake (what renders without script or with reduced motion); the loop goes right → wrong and back, so the
 * tile shows the thing going wrong over and over rather than only its result.
 */
const MISTAKE_STATES: Record<string, { right: gsap.TweenVars; wrong: gsap.TweenVars }> = {
  corner: { right: { x: -32, y: -24 }, wrong: { x: 0, y: 0 } },
  angle: {
    right: { rotation: 16, transformOrigin: "50% 50%" },
    wrong: { rotation: 0, transformOrigin: "50% 50%" },
  },
  glare: { right: { opacity: 0 }, wrong: { opacity: 1 } },
  contrast: { right: { opacity: 1 }, wrong: { opacity: 0 } },
  offcenter: { right: { x: -34, y: -6 }, wrong: { x: 0, y: 0 } },
  tilted: {
    right: { rotation: -20, transformOrigin: "50% 100%" },
    wrong: { rotation: 0, transformOrigin: "50% 100%" },
  },
  glasses: { right: { opacity: 0, y: -6 }, wrong: { opacity: 1, y: 0 } },
  document: { right: { opacity: 0, y: 12 }, wrong: { opacity: 1, y: 0 } },
  crowd: { right: { opacity: 0, x: -10 }, wrong: { opacity: 1, x: 0 } },
};

const BLUR = 1.9;

// The scenes are photos of physical things — a table, a card, a face — so they keep their own colours in both
// themes, the way a photo would. Only the tile around them and the captions follow the tokens.
const DARK_TABLE = "#33403a";
const LIGHT_TABLE = "#e9e6dd";
const CARD = "#f4f6f2";
const CARD_EDGE = "#cfd8d2";
const BRAND = "#2f7d5b";
const INK = "#a6b3ac";
const WALL = "#e4eae6";
const GUIDE = "#7fb59a";
const SKIN = "#e3c3a3";
const SKIN_SHADE = "#cfa985";
const HAIR = "#3a2e28";
const SHIRT = "#2f7d5b";
const SHIRT_SHADE = "#276a4d";
const OTHER_SHIRT = "#8a9a92";

/** The camera's frame, drawn as corner marks: whatever crosses them is out of the photo. */
function FrameCorners() {
  return (
    <path
      d="M6 15 V6 H15 M105 6 H114 V15 M114 75 V84 H105 M15 84 H6 V75"
      fill="none"
      stroke={GUIDE}
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  );
}

function IdCard({ faint = false }: { faint?: boolean }) {
  return (
    <g>
      <rect x="25" y="22" width="70" height="46" rx="4" fill={CARD} stroke={faint ? "#e3e1d8" : CARD_EDGE} />
      <path d="M29 22 H91 A4 4 0 0 1 95 26 V29 H25 V26 A4 4 0 0 1 29 22 Z" fill={BRAND} />
      <rect x="30" y="34" width="15" height="19" rx="2" fill="#c8d3cd" />
      <circle cx="37.5" cy="41" r="3.6" fill="#9fb0a7" />
      <path d="M32 53 C 33 48, 42 48, 43 53 Z" fill="#9fb0a7" />
      <rect x="50" y="35" width="32" height="2.6" rx="1.3" fill={INK} />
      <rect x="50" y="40.5" width="24" height="2.6" rx="1.3" fill={INK} />
      <rect x="50" y="46" width="28" height="2.6" rx="1.3" fill={INK} />
      <rect x="31" y="58" width="58" height="2" rx="1" fill="#c2ccc6" />
      <rect x="31" y="62" width="50" height="2" rx="1" fill="#c2ccc6" />
      <circle cx="86" cy="50" r="4.2" fill="#d7e6ee" />
    </g>
  );
}

function DocumentScene({ example }: { example: DocumentExample }) {
  const uid = useId().replace(/:/g, "");
  const outer =
    example === "corner" ? "translate(32 24)" : example === "angle" ? "rotate(-16 60 45)" : undefined;
  return (
    <>
      <defs>
        <filter id={`${uid}-blur`} x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation={BLUR} />
        </filter>
        <radialGradient id={`${uid}-glare`}>
          <stop offset="0%" stopColor="#ffffff" stopOpacity="1" />
          <stop offset="60%" stopColor="#ffffff" stopOpacity="0.92" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
        </radialGradient>
      </defs>
      <rect width="120" height="90" fill={example === "contrast" ? LIGHT_TABLE : DARK_TABLE} />
      {example === "contrast" ? (
        <rect data-mistake width="120" height="90" fill={DARK_TABLE} opacity="0" />
      ) : null}
      <g transform={outer}>
        <g data-mistake={example === "corner" || example === "angle" ? "" : undefined}>
          <g filter={example === "blurry" ? `url(#${uid}-blur)` : undefined}>
            <IdCard faint={example === "contrast"} />
          </g>
          {example === "glare" ? (
            <ellipse data-mistake cx="66" cy="42" rx="31" ry="19" fill={`url(#${uid}-glare)`} />
          ) : null}
        </g>
      </g>
      <FrameCorners />
    </>
  );
}

function Person({ tilted = false, other = false }: { tilted?: boolean; other?: boolean }) {
  return (
    <g>
      <path d="M30 92 C 31 74, 44 67, 60 67 C 76 67, 89 74, 90 92 Z" fill={other ? OTHER_SHIRT : SHIRT} />
      <path
        d="M50 68.5 Q 60 75 70 68.5"
        fill="none"
        stroke={other ? "#76857e" : SHIRT_SHADE}
        strokeWidth="2"
      />
      <rect x="54.5" y="54" width="11" height="15" rx="4" fill={SKIN_SHADE} />
      <g transform={tilted ? "rotate(20 60 60)" : undefined}>
        <g data-mistake={tilted ? "" : undefined}>
          <ellipse cx="46.6" cy="43.5" rx="2.6" ry="3.6" fill={SKIN_SHADE} />
          <ellipse cx="73.4" cy="43.5" rx="2.6" ry="3.6" fill={SKIN_SHADE} />
          <ellipse cx="60" cy="42" rx="13.5" ry="16" fill={SKIN} />
          <path
            d="M46.4 42.5 C 44.5 22, 75.5 22, 73.6 42.5 C 72.6 35, 69 31.5, 63 31 C 58 33.5, 52 34, 47.6 36.5 C 47 38.5, 46.6 40.5, 46.4 42.5 Z"
            fill={HAIR}
          />
          <circle cx="55" cy="44" r="1.5" fill="#2b2b2b" />
          <circle cx="65" cy="44" r="1.5" fill="#2b2b2b" />
          <path
            d="M56 51 Q 60 54 64 51"
            fill="none"
            stroke="#8a5a4a"
            strokeWidth="1.2"
            strokeLinecap="round"
          />
        </g>
      </g>
    </g>
  );
}

function SelfieScene({ example }: { example: SelfieExample }) {
  return (
    <>
      <rect width="120" height="90" fill={WALL} />
      {/* The camera's face guide: the face belongs inside it. */}
      <ellipse
        cx="60"
        cy="44"
        rx="22"
        ry="27"
        fill="none"
        stroke={GUIDE}
        strokeWidth="1.5"
        strokeDasharray="3 3"
      />
      {example === "crowd" ? (
        <g data-mistake>
          <g transform="translate(-34 8) translate(60 92) scale(0.8) translate(-60 -92)">
            <Person other />
          </g>
        </g>
      ) : null}
      <g transform={example === "offcenter" ? "translate(34 6)" : undefined}>
        <g data-mistake={example === "offcenter" ? "" : undefined}>
          <Person tilted={example === "tilted"} />
          {example === "glasses" ? (
            <g data-mistake>
              <path d="M44.5 35 C 46 21, 74 21, 75.5 35 Z" fill="#1f2a44" />
              <rect x="42" y="33" width="36" height="3.5" rx="1.75" fill="#1f2a44" />
              <rect
                x="49.5"
                y="40.5"
                width="10"
                height="7"
                rx="2.5"
                fill="none"
                stroke="#1d1d1d"
                strokeWidth="1.6"
              />
              <rect
                x="60.5"
                y="40.5"
                width="10"
                height="7"
                rx="2.5"
                fill="none"
                stroke="#1d1d1d"
                strokeWidth="1.6"
              />
              <path d="M59.5 43 H60.5" stroke="#1d1d1d" strokeWidth="1.6" />
            </g>
          ) : null}
          {example === "document" ? (
            <g data-mistake>
              <g transform="rotate(-10 92 66)">
                <rect x="80" y="56" width="26" height="17" rx="2" fill={CARD} stroke={CARD_EDGE} />
                <path d="M82 56 H104 A2 2 0 0 1 106 58 V60 H80 V58 A2 2 0 0 1 82 56 Z" fill={BRAND} />
                <rect x="83" y="62.5" width="6" height="8" rx="1" fill="#c8d3cd" />
                <rect x="91" y="63.5" width="12" height="1.6" rx="0.8" fill={INK} />
                <rect x="91" y="67" width="9" height="1.6" rx="0.8" fill={INK} />
              </g>
              <ellipse cx="84" cy="75" rx="5" ry="4.2" fill={SKIN} />
            </g>
          ) : null}
        </g>
      </g>
    </>
  );
}

interface ExampleTileProps {
  ok: boolean;
  caption: string;
  scene: ReactNode;
  variant: string;
}

function ExampleTile({ ok, caption, scene, variant }: ExampleTileProps) {
  const BadgeIcon = ok ? Check : X;
  return (
    <li data-reveal data-example={variant} className="min-w-0">
      <div
        className={cn(
          "relative overflow-hidden rounded-lg border bg-card",
          ok ? "border-success/60 ring-2 ring-success/25" : "border-border",
        )}
      >
        <svg viewBox="0 0 120 90" role="img" aria-label={caption} className="block h-auto w-full">
          {scene}
        </svg>
        <span
          data-badge
          className={cn(
            "absolute left-1.5 top-1.5 inline-flex size-5 items-center justify-center rounded-full shadow-sm",
            ok ? "bg-success text-white" : "bg-destructive text-white",
          )}
          aria-hidden="true"
        >
          <BadgeIcon className="size-3" strokeWidth={3} />
        </span>
      </div>
      <p
        className={cn(
          "mt-1.5 text-[11px] leading-snug sm:text-xs",
          ok ? "font-medium text-foreground" : "text-muted-foreground",
        )}
      >
        {caption}
      </p>
    </li>
  );
}

export interface CapturePhotoExamplesProps {
  kind: "document" | "selfie";
  className?: string;
}

/**
 * The guide's "así sí / así no" pictures, drawn: the correct photo first, then the mistakes that send a
 * verification to manual review. Each mistake tile loops from the correct photo into its mistake and back —
 * the corner slides out of the frame, the flash flares, the head leaves the face guide — and its ✕ appears
 * with the mistake. The drawn end state is the mistake, so without script or with reduced motion every tile
 * already shows it.
 */
export function CapturePhotoExamples({ kind, className }: CapturePhotoExamplesProps) {
  const { t } = useTranslations("verification");
  const ref = useRef<HTMLUListElement>(null);
  const reduced = usePrefersReducedMotion();

  useLayoutEffect(() => {
    const root = ref.current;
    if (!root || reduced) return;
    const ctx = gsap.context(() => {
      const tiles = Array.from(root.querySelectorAll<HTMLElement>("[data-example]"));
      tiles.forEach((tile, index) => {
        const variant = tile.dataset.example ?? "";
        const badge = tile.querySelector("[data-badge]");
        if (variant === "ok") {
          gsap.fromTo(
            badge,
            { scale: 0.3, opacity: 0 },
            { scale: 1, opacity: 1, duration: MOTION_DURATION.slow, ease: MOTION_EASE.pop, delay: 0.6 },
          );
          return;
        }

        // Tiles start one after another, so the row never moves in lockstep.
        const loop = gsap.timeline({ repeat: -1, yoyo: true, repeatDelay: 1.6, delay: 0.9 + index * 0.35 });
        if (variant === "blurry") {
          const blur = tile.querySelector("feGaussianBlur");
          if (blur) {
            loop.fromTo(
              blur,
              { attr: { stdDeviation: 0 } },
              { attr: { stdDeviation: BLUR }, duration: 0.9, ease: "power2.inOut" },
              0,
            );
          }
        } else {
          const states = MISTAKE_STATES[variant];
          const targets = tile.querySelectorAll("[data-mistake]");
          if (states && targets.length) {
            loop.fromTo(targets, states.right, { ...states.wrong, duration: 0.9, ease: "power2.inOut" }, 0);
          }
        }
        // The ✕ belongs to the mistake: it shows up with it and leaves when the photo is right again.
        if (badge) {
          loop.fromTo(
            badge,
            { opacity: 0, scale: 0.5 },
            { opacity: 1, scale: 1, duration: MOTION_DURATION.quick, ease: MOTION_EASE.enter },
            0.7,
          );
        }
      });
    }, root);
    return () => ctx.revert();
  }, [reduced, kind]);

  const tiles =
    kind === "document"
      ? DOCUMENT_ORDER.map((example) => (
          <ExampleTile
            key={example}
            variant={example}
            ok={example === "ok"}
            caption={t(DOCUMENT_CAPTIONS[example])}
            scene={<DocumentScene example={example} />}
          />
        ))
      : SELFIE_ORDER.map((example) => (
          <ExampleTile
            key={example}
            variant={example}
            ok={example === "ok"}
            caption={t(SELFIE_CAPTIONS[example])}
            scene={<SelfieScene example={example} />}
          />
        ));

  return (
    <ul ref={ref} className={cn("grid grid-cols-3 gap-x-2 gap-y-3 sm:gap-x-2.5", className)}>
      {tiles}
    </ul>
  );
}
